import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import onboardingRocket from "assets/onboardingRocket.gif";
import { UpArrowIcon } from "components";
import {
  useGetOnboarding,
  useGlobalStore,
  useNonPersistantMainStore,
  useSetMspFeatureFlagAsSeen,
} from "controller";
import { OnboardingDrawer } from "pages/modules/onboarding/components/OnboardingDrawer";
import { DRAWER_STEPS } from "pages/modules/onboarding/components/OnboardingDrawer/drawerSteps";
import { useEffect, useMemo, useState } from "react";

export const OnboardingProgressButton = ({
  open,
  isActive = false,
}: {
  open: boolean;
  isActive?: boolean;
}) => {
  const theme = useTheme();
  const isOnboardingDrawerOpen = useNonPersistantMainStore(
    (state) => state.isOnboardingDrawerOpen,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Open drawer automatically 1 minute after being triggered from onboarding modal completion
  useEffect(() => {
    if (!isOnboardingDrawerOpen) return;

    const timerId = setTimeout(() => {
      useNonPersistantMainStore.setState({ isOnboardingDrawerOpen: false });
      setIsDrawerOpen(true);
    }, 60000);

    return () => clearTimeout(timerId);
  }, [isOnboardingDrawerOpen]);

  const { data: onboardingData } = useGetOnboarding();
  const stepsProgressData = onboardingData?.setupSequence || [];

  const featureFlagsFromStore = useNonPersistantMainStore(
    (state) => state.featureFlags,
  );

  const completedCount = useMemo(() => {
    let count = 0;
    for (const step of DRAWER_STEPS) {
      const apiStep = stepsProgressData.find(
        (s: { stage_key: string; is_completed: boolean }) =>
          s.stage_key === step.key,
      );
      if (apiStep?.is_completed) count++;
    }
    return count;
  }, [stepsProgressData]);

  const totalSteps = DRAWER_STEPS.length;
  const progressPercentage = (completedCount / totalSteps) * 100;

  const { mutateAsync: setMspFeatureAsSeen } = useSetMspFeatureFlagAsSeen();
  useEffect(() => {
    if (progressPercentage === 100) {
      setMspFeatureAsSeen("show_onboarding_button");
    }
  }, [progressPercentage]);

  const role = useGlobalStore((state) => state.role);

  if (
    featureFlagsFromStore?.show_onboarding_button !== true ||
    role !== "SUPERADMIN"
  ) {
    return null;
  }

  return (
    <>
      <Box
        onClick={() => setIsDrawerOpen(true)}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: open ? "start" : "center",
          alignItems: "center",
          gap: "20px",
          padding: open ? "8px 16px" : "8px 0px",
          mx: open ? "12px" : "16px",
          borderRadius: "8px",
          bgcolor: open ? theme.palette.neutral.main : "transparent",
          cursor: "pointer",
          position: "relative",
        }}
      >
        {open ? (
          <Stack gap="8px" width="100%">
            <Stack direction="row" alignItems="center" gap="8px">
              <Stack alignItems="center" borderRadius="50%">
                <img
                  src={onboardingRocket}
                  alt="onboarding progress rocket"
                  width="24px"
                  height="24px"
                />
              </Stack>
              <Stack
                width="100%"
                direction="row"
                justifyContent="space-between"
              >
                <Typography
                  variant="body-medium"
                  color={theme.palette.lightBg.main}
                >
                  Get Started
                </Typography>
              </Stack>
              <UpArrowIcon width="24px" height="24px" sx={{ ml: "auto" }} />
            </Stack>
            <Stack
              borderRadius="4px"
              height="8px"
              bgcolor={theme.palette.lightBg.low}
            >
              <Stack
                height="100%"
                width={`${progressPercentage}%`}
                bgcolor="#5EBB70"
                borderRadius="4px 0 0 4px"
              />
            </Stack>
          </Stack>
        ) : (
          <Box
            sx={{
              position: "relative",
              "& .center_elements": {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              },
            }}
          >
            <CircularProgress
              variant="determinate"
              value={100}
              size={45}
              sx={{
                "--CircularProgress-trackThickness": "7px",
                "--CircularProgress-progressThickness": "7px",
                color: theme.palette.lightBg.low,
                position: "absolute",
                left: "0px",
                top: "0px",
              }}
            />
            <CircularProgress
              variant="determinate"
              value={progressPercentage}
              size={45}
              sx={{
                "--CircularProgress-trackThickness": "7px",
                "--CircularProgress-progressThickness": "7px",
                color: theme.palette.success.main,
              }}
            />
            <img
              className="center_elements"
              src={onboardingRocket}
              alt="onboarding progress rocket"
              width="20px"
              height="20px"
            />
          </Box>
        )}
      </Box>

      <OnboardingDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};
